import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient } from '@services/api'
import { useAuthStore } from '@stores/authStore'
import { ProductPicker } from '../components/ProductPicker'
import { DateInputDMY } from '../components/DateInputDMY'
import { PartyEditModal } from '../components/PartyEditModal'
import { evalArithmetic, isExpression } from '../utils/arithmetic'

type Channel = 'Walk-in' | 'Route' | 'Phone'
type PaymentType = 'Cash' | 'Credit' | 'UPI'

interface Store {
  id: number
  name: string
  phone?: string | null
  address?: string | null
  gstin?: string | null
  location?: string | null
  route?: string | null
  reliabilityGrade: 'A' | 'B' | 'C' | 'New'
  onTimePct: number | null
  outstanding: number
  overdue: number
  overdueDays: number
}

interface SellableItem {
  key: string
  product: string
  packG: number
  unit: string
  label: string
  nameTamil: string | null
  gstRate: number
  onHand: number
  rate: number
  avgCost?: number
  priceSource: string
  fefoBatch: string
  fefoExpiry: string
  daysToExpiry: number
}

const SALE_UNITS = ['kg', 'gms', 'pkt', 'Tin', 'ltr'] as const
interface SaleLine {
  id: string
  key: string
  text: string // what the biller typed in the item box; equals the item label once matched
  unit: string // printed on the invoice; pre-set from the item, editable like Purchase Entry
  qty: number
  rate: number
  gstRate: number
}

interface InvoiceRow {
  id: number
  invoiceNo: string
  customerName: string
  amount: number
  type: PaymentType
  paymentStatus: string
}

const GST_RATES = [0, 5, 12, 18, 28]
const EMPTY_CUSTOMER = { name: '', phone: '', location: '', gstin: '' }
const blankLine = (id: string): SaleLine => ({ id, key: '', text: '', unit: 'pkt', qty: NaN, rate: NaN, gstRate: 0 })
const taxIncluded = (amount: number, gstRate: number) => Math.round((amount - amount / (1 + gstRate / 100)) * 100) / 100
const money = (n: number) => `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const WALKIN = 'walkin'

export function BillingInvoice() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  // ?edit=<id> reopens an existing invoice in this form; saving sends PUT and keeps the invoice number.
  const editId = searchParams.get('edit')

  const [storeSel, setStoreSel] = useState<string>('')
  const [channel, setChannel] = useState<Channel>('Walk-in')
  const [customerSaved, setCustomerSaved] = useState('')
  const [editCustomer, setEditCustomer] = useState(false) // "Edit customer" dialog for the selected store
  const [showNewCustomer, setShowNewCustomer] = useState(false)
  const [newCustomer, setNewCustomer] = useState(EMPTY_CUSTOMER)
  const [savingCustomer, setSavingCustomer] = useState(false)
  const [customerError, setCustomerError] = useState('')
  const [walkin, setWalkin] = useState({ name: '', phone: '' })
  const [lines, setLines] = useState<SaleLine[]>([blankLine('1')])
  const [paymentType, setPaymentType] = useState<PaymentType>('Cash')
  const [upiRef, setUpiRef] = useState('')
  const todayIso = new Date().toLocaleDateString('en-CA') // yyyy-mm-dd in local time
  const [invoiceDate, setInvoiceDate] = useState(todayIso) // back-dating allowed; future dates are not
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [errorMessage, setErrorMessage] = useState('')
  const [success, setSuccess] = useState<{ message: string; id: number; invoiceNo: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { data: storesData, refetch: refetchStores } = useQuery({ queryKey: ['stores'], queryFn: () => apiClient.get<any>('/stores') })
  const { data: itemsData, refetch: refetchItems } = useQuery({ queryKey: ['billing-items'], queryFn: () => apiClient.get<any>('/billing/items') })
  // Sales parked as drafts (rates still to be confirmed) — listed at the top until generated or deleted.
  const { data: draftsData } = useQuery({ queryKey: ['invoice-drafts'], queryFn: () => apiClient.get<any>('/invoices?status=Draft') })
  const draftInvoices: any[] = draftsData?.data?.items || []
  const toDmy = (iso: string) => (/^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : iso)
  const { data: productsData } = useQuery({ queryKey: ['products-list'], queryFn: () => apiClient.get<any>('/products') })
  const catalogue: Array<{ id: number; name: string; nameTamil: string | null; unit: string; category: string | null; hsnCode?: string | null }> = productsData?.data?.items || []
  const { data: todayData, refetch: refetchToday } = useQuery({ queryKey: ['invoices-today'], queryFn: () => apiClient.get<any>('/invoices?today=true') })

  const stores: Store[] = storesData?.data?.items || []
  const baseItems: SellableItem[] = itemsData?.data?.items || []
  const allowNegativeStock: boolean = !!itemsData?.data?.allowNegativeStock
  const repackingEnabled = (useAuthStore((s) => s.user) as any)?.tenant?.repackingEnabled !== false
  const todaysInvoices: InvoiceRow[] = todayData?.data?.items || []
  const todaySummary = todayData?.data?.summary
  const nextInvoiceNo: string = todayData?.data?.nextInvoiceNo || 'INV-…'
  const isWalkin = storeSel === WALKIN
  const selectedStore = stores.find((s) => String(s.id) === storeSel) || null
  const { data: editData, isError: editMissing } = useQuery({ queryKey: ['invoice', editId], queryFn: () => apiClient.get<any>(`/invoices/${editId}`), enabled: !!editId, retry: false })
  const editing = editData?.data
  const isDraftEdit = !!editId && editing?.paymentStatus === 'Draft'
  // Lines on an old invoice may be for a pack that is no longer a sellable item (e.g. 1 kg packs from before a product
  // became sold-as-purchased). Keep them selectable while editing so the invoice can still be saved.
  const items: SellableItem[] = useMemo(() => {
    if (!editing?.lineItems) return baseItems
    const have = new Set(baseItems.map((i) => i.key))
    const legacy: SellableItem[] = (editing.lineItems as any[])
      .filter((l) => !have.has(`${l.product}|${l.packG}`))
      .map((l) => ({ key: `${l.product}|${l.packG}`, product: l.product, packG: l.packG, unit: l.unit, label: l.label || l.product, nameTamil: l.nameTamil || null, gstRate: l.gstRate ?? 0, onHand: 0, rate: l.rate, priceSource: 'invoice', fefoBatch: '—', fefoExpiry: '', daysToExpiry: 0 }))
    return legacy.length ? [...baseItems, ...legacy] : baseItems
  }, [baseItems, editing])
  const itemByKey = useMemo(() => new Map(items.map((i) => [i.key, i])), [items])
  const editLoadedFor = useRef<string | null>(null)
  // Pre-fill the form from the invoice being edited once both it and the sellable items have loaded.
  useEffect(() => {
    if (!editId || !editing || items.length === 0 || editLoadedFor.current === editId) return
    editLoadedFor.current = editId
    setSuccess(null); setErrorMessage(''); setErrors({})
    if (editing.store?.id) { setStoreSel(String(editing.store.id)) } else { setStoreSel(WALKIN); setWalkin({ name: editing.customerName || '', phone: editing.customerPhone || '' }) }
    setChannel((editing.channel as Channel) || 'Walk-in')
    setPaymentType((editing.type as PaymentType) || 'Cash')
    setInvoiceDate(editing.invoiceDate || todayIso)
    setDiscountMode(editing.discountMode === 'percent' ? 'percent' : 'amount'); setDiscountInput(editing.discountValue ? String(editing.discountValue) : '')
    setRateText({})
    setLines((editing.lineItems || []).map((l: any, i: number) => {
      const key = `${l.product}|${l.packG}`
      const it = itemByKey.get(key)
      return { id: String(i + 1), key: it ? key : '', text: it ? it.label : l.label || l.product, unit: l.unit || 'pkt', qty: l.quantity, rate: editing.paymentStatus === 'Draft' && !(Number(l.rate) > 0) ? NaN : l.rate, gstRate: l.gstRate ?? 0 }
    }))
  }, [editId, editing, items])
  const cancelEdit = () => navigate(editId ? `/dashboard/sales/invoices/${editId}` : '/dashboard/sales/invoices')

  // Deep link from Stock for Sale: ?item=<product>|<packG> pre-fills the first line.
  React.useEffect(() => {
    const preset = searchParams.get('item')
    if (!preset || items.length === 0) return
    const it = itemByKey.get(preset)
    if (it) setLines([{ id: '1', key: it.key, text: it.label, unit: it.unit, qty: NaN, rate: it.rate, gstRate: it.gstRate }])
    setSearchParams({}, { replace: true })
  }, [searchParams, items])

  // Selling rates are tax-inclusive (as on the company's invoices): GST is carved out of each line.
  const [discountMode, setDiscountMode] = useState<'percent' | 'amount'>('percent')
  const [discountInput, setDiscountInput] = useState('') // blank = no discount
  const r2 = (n: number) => Math.round(n * 100) / 100
  const lineAmts = lines.map((l) => r2((l.qty || 0) * (l.rate || 0)))
  const subTotal = r2(lineAmts.reduce((s, a) => s + a, 0))
  const discountValue = discountInput.trim() === '' ? 0 : Math.max(0, Number(discountInput) || 0)
  const discountAmount = Math.min(subTotal, discountMode === 'percent' ? r2(subTotal * Math.min(discountValue, 100) / 100) : r2(discountValue))
  const afterDiscount = r2(subTotal - discountAmount)
  // The discount is spread over the lines pro rata (last line takes the rounding), then GST is carved out of what is left.
  const gstAmount = (() => {
    if (!(discountAmount > 0) || subTotal <= 0) return r2(lines.reduce((s, l, i) => s + taxIncluded(lineAmts[i], l.gstRate || 0), 0))
    let left = afterDiscount
    return r2(lines.reduce((s, l, i) => {
      const a = i === lines.length - 1 ? r2(left) : r2(lineAmts[i] * afterDiscount / subTotal)
      left = r2(left - a)
      return s + taxIncluded(a, l.gstRate || 0)
    }, 0))
  })()
  const total = Math.round(afterDiscount)
  const roundOff = r2(total - afterDiscount)
  const clearError = (key: string) => setErrors((prev) => { if (!prev[key]) return prev; const n = { ...prev }; delete n[key]; return n })

  const updateLine = (id: string, patch: Partial<SaleLine>) => {
    setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)))
    Object.keys(patch).forEach((k) => clearError(`line-${id}-${k}`))
  }
  // Raw text typed in each line's Rate box (may be an expression like 820/20); the numeric rate is derived from it.
  const [rateText, setRateText] = useState<Record<string, string>>({})
  const rateDisplay = (l: SaleLine) => (rateText[l.id] !== undefined ? rateText[l.id] : Number.isNaN(l.rate) ? '' : String(l.rate))
  const typeRate = (l: SaleLine, text: string) => {
    setRateText((m) => ({ ...m, [l.id]: text }))
    const v = text.trim() === '' ? NaN : evalArithmetic(text)
    updateLine(l.id, { rate: v === null ? NaN : v })
  }
  const settleRate = (l: SaleLine) => {
    const text = rateText[l.id]
    if (text === undefined) return
    const v = text.trim() === '' ? NaN : evalArithmetic(text)
    setRateText((m) => { const n = { ...m }; if (v === null || Number.isNaN(v)) { if (text.trim() === '') delete n[l.id] } else n[l.id] = String(v); return n })
    if (v !== null && !Number.isNaN(v)) updateLine(l.id, { rate: v })
  }
  const chooseItem = (id: string, key: string) => {
    const it = itemByKey.get(key)
    setRateText((m) => { const n = { ...m }; delete n[id]; return n }) // show the item's own rate, not stale typed text
    updateLine(id, { key, text: it ? it.label : '', unit: it ? it.unit : 'pkt', rate: it ? it.rate : NaN, gstRate: it ? it.gstRate : 0 })
  }
  // Type-to-filter item box: the typed text is matched against item labels; an exact match selects the item.
  const itemByLabel = useMemo(() => new Map(items.map((i) => [i.label.toLowerCase(), i])), [items])
  // Sellable stock first; catalogue products with nothing in stock are listed greyed out so the biller sees why they can't be picked.
  const pickerOptions = useMemo(() => {
    const stocked = items.map((i) => ({ value: i.key, label: i.label, sub: i.nameTamil || null, right: i.onHand > 0 ? `Stock on hand ${i.onHand.toLocaleString('en-IN')} ${i.unit}` : allowNegativeStock ? 'No stock — bills as negative' : 'No stock', rightSub: i.rate > 0 ? `Rate ${money(i.rate)}` : 'enter rate', rightClass: i.onHand > 0 ? 'text-good' : 'text-danger' }))
    const stockedProducts = new Set(items.map((i) => i.product))
    const unstocked = catalogue.filter((p) => !stockedProducts.has(p.name)).map((p) => ({ value: `nostock|${p.name}`, label: p.name, sub: p.nameTamil || null, right: 'No stock', rightSub: p.unit === 'kg' ? 'buy & repack first' : 'enter a purchase first', rightClass: 'text-danger', disabled: true }))
    return [...stocked, ...unstocked]
  }, [items, catalogue])
  const typeItem = (id: string, text: string) => {
    const it = itemByLabel.get(text.trim().toLowerCase())
    if (it) chooseItem(id, it.key)
    else updateLine(id, { text, key: '' })
  }
  const filteredItems = (text: string) => {
    const q = text.trim().toLowerCase()
    if (!q || itemByLabel.has(q)) return items
    const terms = q.split(/\s+/)
    return items.filter((i) => { const hay = `${i.label} ${i.nameTamil || ''}`.toLowerCase(); return terms.every((t) => hay.includes(t)) })
  }
  // ---- Zoho-style "⋯ → Edit item" on every invoice line: change the product's price / GST / HSN / names without leaving billing ----
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [itemModal, setItemModal] = useState<{ lineId: string; mode: 'edit' | 'view' | 'new' } | null>(null)
  const [itemForm, setItemForm] = useState({ name: '', price: '', gstRate: 0, hsnCode: '', nameTamil: '', category: '', unit: 'pkt' })
  const [itemSaving, setItemSaving] = useState(false)
  const [itemError, setItemError] = useState('')
  const modalItem = itemModal ? itemByKey.get(lines.find((x) => x.id === itemModal.lineId)?.key || '') : undefined
  const modalProduct: any = modalItem ? (catalogue as any[]).find((p) => p.name === modalItem.product) : undefined
  const listedFor = (prod: any, packG: number, unit: string) => { const sp = prod?.sellingPrices || {}; const v = sp[String(packG)] ?? (packG === 0 && unit === 'kg' ? sp['1000'] : undefined); return v ? String(v) : '' }
  const openItemModal = (l: SaleLine, mode: 'edit' | 'view') => {
    const item = itemByKey.get(l.key); if (!item) return
    const prod: any = (catalogue as any[]).find((p) => p.name === item.product)
    setItemForm({ name: item.product, price: listedFor(prod, item.packG, item.unit) || (Number.isNaN(l.rate) ? '' : String(l.rate)), gstRate: prod?.gstRate ?? item.gstRate ?? 0, hsnCode: prod?.hsnCode || '', nameTamil: prod?.nameTamil || item.nameTamil || '', category: prod?.category || '', unit: item.unit || 'pkt' })
    setItemError(''); setMenuFor(null); setItemModal({ lineId: l.id, mode })
  }
  // "+ Add new item" from a line's picker: create the product here, then select it on that line.
  const openNewItemModal = (lineId: string, typed: string) => {
    setItemForm({ name: typed.trim(), price: '', gstRate: 5, hsnCode: '', nameTamil: '', category: '', unit: 'pkt' })
    setItemError(''); setMenuFor(null); setItemModal({ lineId, mode: 'new' })
  }
  const saveNewItem = async () => {
    if (!itemModal) return
    const name = itemForm.name.replace(/\s+/g, ' ').trim()
    if (!name) return setItemError('Item name is required')
    const hsn = itemForm.hsnCode.replace(/\s+/g, '')
    if (!/^\d{4,8}$/.test(hsn)) return setItemError('HSN code is required — 4 to 8 digits (e.g. 0713 or 09109100)')
    const price = itemForm.price.trim()
    if (price !== '' && !(Number(price) >= 0)) return setItemError('Selling price must be a number')
    setItemSaving(true); setItemError('')
    try {
      const res = await apiClient.post<any>('/products', { name, unit: itemForm.unit, gstRate: itemForm.gstRate, category: itemForm.category, nameTamil: itemForm.nameTamil, hsnCode: hsn, defaultRoute: 'Direct Sale' })
      const created = res?.data
      if (price !== '' && created?.id) await apiClient.patch<any>(`/products/${created.id}`, { sellingPrices: { '0': price } })
      const [refreshed] = await Promise.all([refetchItems(), queryClient.invalidateQueries({ queryKey: ['products-list'] })])
      const key = `${created?.name || name}|0`
      const found = ((refreshed as any)?.data?.data?.items || []).find((i: any) => i.key === key)
      const lineId = itemModal.lineId
      setItemModal(null)
      if (found) {
        // Fill the line from the refreshed list directly — the item map in this closure predates the refetch.
        setRateText((m) => { const n = { ...m }; delete n[lineId]; return n })
        updateLine(lineId, { key: found.key, text: found.label, unit: found.unit, rate: found.rate, gstRate: found.gstRate })
      } else {
        updateLine(lineId, { text: created?.name || name, key: '' })
        setErrorMessage(`"${name}" was added to the catalogue, but it has no stock yet so it cannot be billed. Record its purchase first, or turn on "Allow billing without stock" under Settings.`)
      }
    } catch (err: any) { setItemError(err.response?.data?.message || 'Could not add the item') } finally { setItemSaving(false) }
  }
  const saveItem = async () => {
    if (!modalItem || !modalProduct) return setItemError('This item is not in the product catalogue, so it cannot be edited here.')
    const price = itemForm.price.trim()
    if (price !== '' && !(Number(price) >= 0)) return setItemError('Selling price must be a number')
    const newName = itemForm.name.replace(/\s+/g, ' ').trim()
    if (!newName) return setItemError('Item name cannot be empty')
    setItemSaving(true); setItemError('')
    try {
      await apiClient.patch<any>(`/products/${modalProduct.id}`, { name: newName, category: itemForm.category, nameTamil: itemForm.nameTamil, hsnCode: itemForm.hsnCode, gstRate: itemForm.gstRate, sellingPrices: { [String(modalItem.packG)]: price } })
      const [refreshed] = await Promise.all([refetchItems(), queryClient.invalidateQueries({ queryKey: ['products-list'] })])
      // Lines for this item pick up the new name, price and GST straight away.
      const newRate = Number(price)
      const newKey = `${newName}|${modalItem.packG}`
      const newLabel = ((refreshed as any)?.data?.data?.items || []).find((i: any) => i.key === newKey)?.label
      setLines((ls) => ls.map((x) => (x.key === modalItem.key ? { ...x, key: newKey, text: newLabel || x.text, gstRate: itemForm.gstRate, ...(newRate > 0 ? { rate: newRate } : {}) } : x)))
      setRateText((m) => { const n = { ...m }; for (const x of lines) if (x.key === modalItem.key) delete n[x.id]; return n })
      setItemModal(null)
    } catch (err: any) { setItemError(err.response?.data?.message || 'Could not save the item') } finally { setItemSaving(false) }
  }
  const focusLineId = useRef<string | null>(null)
  useEffect(() => {
    if (!focusLineId.current) return
    const el = document.querySelector<HTMLInputElement>(`input[data-item-line="${focusLineId.current}"]`)
    focusLineId.current = null
    el?.focus()
  }, [lines])
  const addLine = () => setLines((ls) => { const id = String(Math.max(0, ...ls.map((l) => parseInt(l.id))) + 1); focusLineId.current = id; return [...ls, blankLine(id)] })
  const removeLine = (id: string) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.id !== id) : ls))

  const choosePaymentType = (t: PaymentType) => {
    setPaymentType(t)
    clearError('paymentType')
  }

  const saveCustomer = async () => {
    setCustomerError('')
    if (!newCustomer.name.trim()) return setCustomerError('Shop / customer name is required')
    if (newCustomer.phone && !/^\d{10}$/.test(newCustomer.phone)) return setCustomerError('Phone must be a 10-digit mobile number')
    setSavingCustomer(true)
    try {
      const res = await apiClient.post<any>('/stores', newCustomer)
      await refetchStores()
      setStoreSel(String(res.data.id))
      if (channel === 'Walk-in') setChannel('Route')
      setPaymentType('Credit')
      setShowNewCustomer(false)
      setNewCustomer(EMPTY_CUSTOMER)
      clearError('store')
    } catch (err: any) {
      setCustomerError(err.response?.data?.message || 'Could not save the customer')
    } finally {
      setSavingCustomer(false)
    }
  }

  const validate = (draft = false) => {
    const e: Record<string, string> = {}
    if (!storeSel) e.store = 'Choose a store or walk-in customer'
    if (isWalkin && !walkin.name.trim()) e.walkinName = 'Customer name is required'
    if (isWalkin && walkin.phone && !/^\d{10}$/.test(walkin.phone)) e.walkinPhone = 'Phone must be a 10-digit mobile number'
    if (isWalkin && paymentType === 'Credit') e.paymentType = 'Walk-in customers pay now — choose Cash or UPI (or add them as a shop for credit)'
    if (!isWalkin && storeSel && paymentType !== 'Credit') e.paymentType = 'Registered stores are billed on credit — the amount goes to their ledger'
    if (isWalkin && paymentType === 'UPI' && upiRef && upiRef.trim().length < 6) e.paymentType = 'Enter the full UPI transaction reference'
    if (!/^\d{4}-\d{2}-\d{2}$/.test(invoiceDate)) e.invoiceDate = 'Pick the invoice date'
    else if (invoiceDate > todayIso) e.invoiceDate = 'Invoice date cannot be in the future'
    const real = lines.filter((l) => l.key || l.qty > 0)
    if (real.length === 0) e.lines = 'Add at least one item'
    real.forEach((l) => {
      const it = itemByKey.get(l.key)
      if (!l.key) e[`line-${l.id}-key`] = 'Pick an item'
      if (!(l.qty > 0) || l.qty > 1000000) e[`line-${l.id}-qty`] = 'Enter a quantity above 0'
      else if (Math.abs(l.qty * 1000 - Math.round(l.qty * 1000)) > 1e-6) e[`line-${l.id}-qty`] = 'At most 3 decimals'
      else if (!draft && it && l.qty > it.onHand && !allowNegativeStock) e[`line-${l.id}-qty`] = `Only ${it.onHand.toLocaleString('en-IN')} in stock`
      if (!draft && !(l.rate > 0)) e[`line-${l.id}-rate`] = 'Rate required — or save as draft'
    })
    if (discountMode === 'percent' && discountValue > 100) e.discount = 'Discount cannot be more than 100%'
    else if (discountMode === 'amount' && discountValue > subTotal + 0.005 && subTotal > 0) e.discount = 'Discount is more than the invoice value'
    const keys = real.map((l) => l.key)
    keys.forEach((k, i) => { if (k && keys.indexOf(k) !== i) e[`line-${real[i].id}-key`] = 'Item listed twice' })
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const generate = async (opts: { draft?: boolean; submitAfterSave?: boolean } = {}) => {
    const asDraft = !!opts.draft
    setErrorMessage('')
    setSuccess(null)
    if (!validate(asDraft)) return
    setSubmitting(true)
    try {
      const real = lines.filter((l) => l.key)
      const payload = {
        storeId: isWalkin ? null : Number(storeSel),
        walkin: isWalkin ? { name: walkin.name.trim(), phone: walkin.phone.trim() } : undefined,
        channel,
        paymentType,
        invoiceDate,
        upiReference: paymentType === 'UPI' && upiRef.trim() ? upiRef.trim() : undefined,
        status: asDraft ? 'Draft' : undefined,
        discountMode,
        discountValue,
        lines: real.map((l) => { const it = itemByKey.get(l.key)!; return { product: it.product, packG: it.packG, unit: l.unit, quantity: l.qty, rate: Number.isNaN(l.rate) && asDraft ? 0 : l.rate, gstRate: l.gstRate } }),
      }
      if (editId) {
        const res = await apiClient.put<any>(`/invoices/${editId}`, payload)
        let message = res.message
        if (opts.submitAfterSave) {
          // "Save & generate" on a draft: the corrected lines are saved first, then the real invoice is generated.
          const sub = await apiClient.post<any>(`/invoices/${editId}/submit`, {})
          message = sub.message || message
        }
        queryClient.invalidateQueries({ queryKey: ['invoice', editId] })
        for (const k of ['invoices', 'invoice-drafts', 'invoices-today', 'billing-items', 'stock-overview', 'home-dashboard', 'stores']) queryClient.invalidateQueries({ queryKey: [k] })
        navigate(`/dashboard/sales/invoices/${editId}`, { state: { message } })
        return
      }
      const res = await apiClient.post<any>('/invoices', payload)
      setSuccess({ message: res.message, id: res.data.id, invoiceNo: res.data.invoiceNo })
      setRateText({})
      setDiscountMode('percent'); setDiscountInput('')
      setLines([blankLine('1')])
      // Back to a blank sale: the next customer is chosen afresh (the invoice date is kept for back-dated entry).
      setStoreSel('')
      setChannel('Walk-in')
      setWalkin({ name: '', phone: '' })
      setPaymentType('Cash')
      setUpiRef('')
      setErrors({})
      await Promise.all([refetchItems(), refetchToday(), refetchStores()])
      queryClient.invalidateQueries({ queryKey: ['invoice-drafts'] })
      queryClient.invalidateQueries({ queryKey: ['stock-overview'] })
      queryClient.invalidateQueries({ queryKey: ['home-dashboard'] })
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Could not generate the invoice')
    } finally {
      setSubmitting(false)
    }
  }

  const gradeText = (s: Store) => (s.reliabilityGrade === 'New' ? 'New customer · no credit history yet' : `Grade ${s.reliabilityGrade} · ${s.onTimePct}% on-time`)

  return (
    <div>
      {editCustomer && selectedStore && (
        <PartyEditModal
          kind="customer"
          initial={{ name: selectedStore.name, phone: selectedStore.phone || '', address: selectedStore.address || '', location: selectedStore.location || '', gstin: selectedStore.gstin || '', email: (selectedStore as any).email || '', route: selectedStore.route || '', creditDays: String((selectedStore as any).creditDays ?? 30) }}
          onClose={() => setEditCustomer(false)}
          onSave={async (v) => {
            const res = await apiClient.patch<any>(`/stores/${selectedStore.id}`, { name: v.name, phone: v.phone, address: v.address, location: v.location, gstin: v.gstin, email: v.email, route: v.route, creditDays: v.creditDays })
            await refetchStores()
            for (const k of ['stores', 'invoices', 'invoices-today', 'invoice-drafts', 'home-dashboard']) queryClient.invalidateQueries({ queryKey: [k] })
            setEditCustomer(false)
            setSuccess(null)
            setErrorMessage('')
            setCustomerSaved(res.message || 'Customer details saved')
            setTimeout(() => setCustomerSaved(''), 4000)
          }}
        />
      )}
      {itemModal && (modalItem || itemModal.mode === 'new') && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => !itemSaving && setItemModal(null)}>
          <div className="card" style={{ width: 'min(520px, 100%)', maxHeight: '90vh', overflow: 'auto', margin: 0 }} onClick={(e) => e.stopPropagation()} data-testid="item-modal">
            <div className="field-label-row">
              <h2 style={{ marginBottom: 0 }}>{itemModal.mode === 'new' ? 'Add new item' : itemModal.mode === 'edit' ? 'Edit item' : 'Item details'}</h2>
              <button type="button" className="btn-ghost" style={{ padding: '2px 9px' }} onClick={() => setItemModal(null)} aria-label="Close">×</button>
            </div>
            <label style={{ marginTop: 8 }}>Item name</label>
            <input type="text" disabled={itemModal.mode === 'view'} value={itemForm.name} data-testid="item-name" onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} />
            <div className="hint" style={{ marginTop: 0 }}>{modalItem ? <>{modalItem.label}{itemModal.mode === 'edit' ? ' — renaming also updates this item in past bills, invoices and stock.' : ''}</> : 'New catalogue product — it is added to the list and placed on this invoice line.'}</div>
            {modalItem && <div className="hint" style={{ marginTop: 2 }}>On hand {modalItem.onHand.toLocaleString('en-IN')} {modalItem.unit} · {modalItem.avgCost ? `avg cost ${money(modalItem.avgCost)}` : 'no cost yet'} · billing rate now {money(Number.isNaN(lines.find((x) => x.id === itemModal.lineId)?.rate as number) ? 0 : (lines.find((x) => x.id === itemModal.lineId)?.rate as number))} ({modalItem.priceSource})</div>}
            {itemError && <div className="alert alert-error" style={{ marginTop: 8 }}>{itemError}</div>}
            {itemModal.mode !== 'new' && !modalProduct && <div className="alert alert-error" style={{ marginTop: 8 }}>This item is not in the product catalogue, so it cannot be edited here.</div>}
            <div className="grid grid-cols-2 gap-3" style={{ marginTop: 10 }}>
              <div>
                <label>Selling price / MRP ₹ <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>— incl. GST</small></label>
                <input type="text" inputMode="decimal" autoComplete="off" disabled={itemModal.mode === 'view'} value={itemForm.price} data-testid="item-price" onChange={(e) => setItemForm({ ...itemForm, price: e.target.value.replace(/[^\d.]/g, '') })} />
              </div>
              <div>
                <label>GST %</label>
                <select disabled={itemModal.mode === 'view'} value={itemForm.gstRate} onChange={(e) => setItemForm({ ...itemForm, gstRate: Number(e.target.value) })}>{GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}</select>
              </div>
              {itemModal.mode === 'new' && (
                <div>
                  <label>Unit</label>
                  <select value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} data-testid="item-unit">{['kg', 'gms', 'pkt', 'Tin', 'ltr'].map((u) => <option key={u} value={u}>{u}</option>)}</select>
                </div>
              )}
              <div><label>HSN{itemModal.mode === 'new' ? <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}> — required, 4–8 digits</small> : null}</label><input type="text" inputMode="numeric" disabled={itemModal.mode === 'view'} value={itemForm.hsnCode} data-testid="item-hsn" onChange={(e) => setItemForm({ ...itemForm, hsnCode: e.target.value.replace(/\D/g, '').slice(0, 8) })} /></div>
              <div><label>Category</label><input type="text" disabled={itemModal.mode === 'view'} value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} /></div>
            </div>
            <label>Tamil name</label>
            <input type="text" disabled={itemModal.mode === 'view'} value={itemForm.nameTamil} onChange={(e) => setItemForm({ ...itemForm, nameTamil: e.target.value })} />
            <div className="btnrow">
              {itemModal.mode === 'edit' && <button type="button" className="btn-accent" disabled={itemSaving || !modalProduct} onClick={saveItem} data-testid="item-save">{itemSaving ? 'Saving…' : 'Save'}</button>}
              {itemModal.mode === 'new' && <button type="button" className="btn-accent" disabled={itemSaving} onClick={saveNewItem} data-testid="item-save">{itemSaving ? 'Adding…' : 'Add item & use on this line'}</button>}
              <button type="button" className="btn-ghost" disabled={itemSaving} onClick={() => setItemModal(null)}>{itemModal.mode === 'view' ? 'Close' : 'Cancel'}</button>
            </div>
            <div className="hint">The selling price becomes the default rate for this item on every future invoice. Prices can also be updated in bulk under Settings → Products &amp; Categories.</div>
          </div>
        </div>
      )}
      <div className="hdr">
        <h1>{editId ? `Edit ${editing?.invoiceNo || 'invoice'}` : 'Billing / Invoice'}</h1>
        <p>{editId ? (isDraftEdit ? 'Fill in the rates and generate the invoice, or save it again as a draft.' : 'Change the customer, date or items and save — the invoice number stays the same and stock is re-posted from the new lines.') : 'Create a sale — walk-in, phone order, or route sale — and generate a GST invoice.'}</p>
      </div>
      {draftInvoices.filter((d) => String(d.id) !== editId).length > 0 && (
        <div className="card" style={{ borderColor: '#E9C46A', background: '#FFFBEB', marginBottom: 18 }} data-testid="invoice-drafts-panel">
          <h2>
            Draft invoices waiting <small>— {draftInvoices.filter((d) => String(d.id) !== editId).length} saved as draft; no stock or ledger entry until generated. Open one to fill in the rates and generate it.</small>
          </h2>
          <table>
            <thead>
              <tr>
                <th>Draft</th>
                <th>Customer</th>
                <th>Date</th>
                <th className="num">Items</th>
                <th className="num">Total so far</th>
                <th>Saved by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {draftInvoices.filter((d) => String(d.id) !== editId).map((d: any) => (
                <tr key={d.id}>
                  <td><Link to={`/dashboard/sales/invoices/${d.id}`} className="text-primary-500 font-semibold hover:underline">{d.invoiceNo}</Link></td>
                  <td>{d.customerName}</td>
                  <td>{toDmy(d.invoiceDate)}</td>
                  <td className="num">{d.itemCount}</td>
                  <td className="num font-semibold">{money(d.amount)}</td>
                  <td className="text-ink-soft text-[12px]">{d.createdBy}</td>
                  <td className="text-right whitespace-nowrap">
                    <Link to={`/dashboard/sales/invoices?edit=${d.id}`} className="btn-accent btn-xs" title="Open the draft to fill in the rates and generate it">Open & generate</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <div className="card">
          <h2>{editId ? `Editing ${editing?.invoiceNo || ''}` : 'New Sale'}</h2>
          {editId && editMissing && <div className="alert alert-error">That invoice could not be loaded. <Link to="/dashboard/sales/invoices/list" className="underline">Back to invoices</Link></div>}
          {success && (
            <div className="alert alert-success">
              {success.message} · <Link to={`/dashboard/sales/invoices/${success.id}`} className="font-semibold underline">Open {success.invoiceNo}</Link>
            </div>
          )}
          {errorMessage && <div className="alert alert-error">{errorMessage}</div>}
          {customerSaved && <div className="alert alert-success" data-testid="customer-saved">{customerSaved}</div>}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="field-label-row">
                <label style={{ margin: 0 }}>Store / Customer</label>
                <button type="button" className="btn-ghost" style={{ padding: '4px 10px', fontSize: 11.5 }} onClick={() => setShowNewCustomer((v) => !v)}>
                  + Add new customer/shop
                </button>
              </div>
              <select
                id="invoiceStoreSelect"
                value={storeSel}
                onChange={(e) => {
                  setStoreSel(e.target.value)
                  clearError('store')
                  clearError('paymentType')
                  if (e.target.value === WALKIN) {
                    setChannel('Walk-in')
                    setPaymentType('Cash') // walk-ins pay now: Cash or UPI
                  } else if (e.target.value) {
                    if (channel === 'Walk-in') setChannel('Route')
                    setPaymentType('Credit') // registered stores buy on credit → store ledger
                  }
                }}
                className={errors.store ? 'error' : ''}
              >
                <option value="">— Select store —</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.location ? `, ${s.location}` : ''}</option>
                ))}
                <option value={WALKIN}>— Walk-in / One-time Customer (no shop record) —</option>
              </select>
              {errors.store && <div className="hint-error">{errors.store}</div>}
              {selectedStore && (
                <div data-testid="customer-details" style={{ marginTop: 8, padding: '8px 10px', border: '1px solid var(--border, #ddd)', borderRadius: 8, background: 'var(--primary-soft, #f3f6fb)', fontSize: 12, lineHeight: 1.5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{selectedStore.name}</div>
                    <button type="button" className="btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => setEditCustomer(true)} data-testid="edit-customer" title="Change this customer's name, phone, address or GSTIN">✎ Edit</button>
                  </div>
                  <div><span className="text-ink-soft">Address: </span>{selectedStore.address || selectedStore.location || <span className="text-ink-soft">not saved</span>}</div>
                  <div><span className="text-ink-soft">Phone: </span>{selectedStore.phone || <span className="text-ink-soft">not saved</span>}{selectedStore.gstin ? <span className="text-ink-soft"> · GSTIN {selectedStore.gstin}</span> : null}</div>
                </div>
              )}
              {selectedStore && (
                <span className={`rel-badge ${selectedStore.reliabilityGrade === 'New' ? 'B' : selectedStore.reliabilityGrade}`} style={{ marginTop: 6, display: 'inline-flex' }}>
                  {gradeText(selectedStore)}
                </span>
              )}
              {selectedStore && selectedStore.outstanding > 0 && (
                <div className={`hint ${selectedStore.overdue > 0 ? 'hint-error' : ''}`} style={{ marginTop: 4 }}>
                  Outstanding {formatINR(selectedStore.outstanding)}{selectedStore.overdue > 0 ? ` · ${formatINR(selectedStore.overdue)} overdue by ${selectedStore.overdueDays} days` : ''}
                </div>
              )}
            </div>
            <div>
              <label>Order Channel</label>
              <select value={channel} onChange={(e) => setChannel(e.target.value as Channel)}>
                <option>Walk-in</option>
                <option>Route</option>
                <option>Phone</option>
              </select>
              <div className="hint">Route sales are grouped by the store's route on Sales &amp; Collections.</div>
            </div>
            <div>
              <label>Invoice Date</label>
              <DateInputDMY value={invoiceDate} max={todayIso} testId="invoice-date" onChange={(iso) => { setInvoiceDate(iso); clearError('invoiceDate') }} className={errors.invoiceDate ? 'error' : ''} />
              {errors.invoiceDate ? <div className="hint-error">{errors.invoiceDate}</div> : (
                <div className="hint">{editId ? `Number ${editing?.invoiceNo || ''} is kept.` : `Next number ${nextInvoiceNo} is assigned on generate.`} Pick an earlier date to record a back-dated sale.</div>
              )}
            </div>
          </div>

          {showNewCustomer && (
            <div style={{ background: 'var(--primary-soft)', borderRadius: 'var(--radius)', padding: 14, marginTop: 12 }}>
              {customerError && <div className="alert alert-error">{customerError}</div>}
              <div className="row2">
                <div><label>Shop / Customer Name</label><input placeholder="e.g. Ganesh Provision Store" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} /></div>
                <div><label>Phone</label><input placeholder="10-digit mobile" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} /></div>
              </div>
              <div className="row2">
                <div><label>Location / Area</label><input placeholder="e.g. Palayamkottai" value={newCustomer.location} onChange={(e) => setNewCustomer({ ...newCustomer, location: e.target.value })} /></div>
                <div><label>GSTIN <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>— optional</small></label><input value={newCustomer.gstin} onChange={(e) => setNewCustomer({ ...newCustomer, gstin: e.target.value })} /></div>
              </div>
              <button type="button" className="btn-accent" style={{ marginTop: 10, padding: '8px 14px' }} onClick={saveCustomer} disabled={savingCustomer}>
                {savingCustomer ? 'Saving…' : 'Save & Use for This Sale'}
              </button>
              <div className="hint">Saved as a permanent store record — appears in the list above for every future sale.</div>
            </div>
          )}

          {isWalkin && (
            <div style={{ background: 'var(--accent-soft)', borderRadius: 'var(--radius)', padding: 14, marginTop: 12 }}>
              <div className="row2">
                <div>
                  <label>Customer Name</label>
                  <input placeholder="e.g. Ramesh (cash counter)" value={walkin.name} onChange={(e) => { setWalkin({ ...walkin, name: e.target.value }); clearError('walkinName') }} className={errors.walkinName ? 'error' : ''} />
                  {errors.walkinName && <div className="hint-error">{errors.walkinName}</div>}
                </div>
                <div>
                  <label>Phone <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>— optional</small></label>
                  <input placeholder="10-digit mobile" value={walkin.phone} onChange={(e) => { setWalkin({ ...walkin, phone: e.target.value }); clearError('walkinPhone') }} className={errors.walkinPhone ? 'error' : ''} />
                  {errors.walkinPhone && <div className="hint-error">{errors.walkinPhone}</div>}
                </div>
              </div>
              <div className="hint">One-time entry for this bill only — no permanent shop record created, no credit ledger, no reliability score. Use "+ Add new customer/shop" instead if this buyer will order again.</div>
            </div>
          )}

          {itemsData && items.length === 0 && (
            <div className="alert alert-error mt-[14px]" style={{ background: 'var(--accent-soft)', color: 'var(--ink)', borderColor: 'var(--accent)' }}>
              <b>Nothing in stock to sell yet.</b> Items appear here once stock exists: enter the supplier bill in <Link to="/dashboard/procurement/purchase-bills" className="font-semibold underline">Purchase Entry</Link>.{repackingEnabled ? <> Products bought by the kg go to <Link to="/dashboard/repacking" className="font-semibold underline">Repacking</Link> first and become sellable as packs; products bought per piece are sellable immediately.</> : ' Every purchased item becomes sellable as soon as its bill is submitted.'}{catalogue.length > 0 ? ` Your catalogue has ${catalogue.length} products — they show below greyed out until they have stock.` : ''}
            </div>
          )}
          <table className="mt-[14px]">
            <thead>
              <tr>
                <th>Item</th>
                <th>Unit</th>
                <th className="num">Qty</th>
                <th className="num">Rate (incl. GST)</th>
                <th className="num">GST%</th>
                <th className="num">Amount</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const it = itemByKey.get(l.key)
                return (
                  <tr key={l.id}>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ProductPicker
                            options={pickerOptions}
                            text={l.text}
                            onText={(t) => typeItem(l.id, t)}
                            onSelect={(o) => chooseItem(l.id, o.value)}
                            placeholder="Type or click to select an item"
                            className={`!py-[6px] ${errors[`line-${l.id}-key`] ? '!border-danger' : ''}`}
                            inputProps={{ 'data-item-line': l.id, style: { marginBottom: 0 } }}
                            emptyText="No item matches — check Stock for Sale"
                            onAddNew={(typed) => openNewItemModal(l.id, typed)}
                          />
                        </div>
                        {it && (
                          <div style={{ position: 'relative' }}>
                            <button type="button" className="btn-ghost" style={{ padding: '4px 9px', lineHeight: 1 }} title="Item options" aria-label="Item options" data-testid="line-menu" onClick={() => setMenuFor(menuFor === l.id ? null : l.id)}>⋯</button>
                            {menuFor === l.id && (
                              <>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setMenuFor(null)} />
                                <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 41, background: '#fff', border: '1px solid var(--border, #ddd)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,.15)', minWidth: 170, padding: 4 }}>
                                  <button type="button" className="btn-primary" style={{ width: '100%', textAlign: 'left', marginBottom: 2 }} data-testid="line-edit-item" onClick={() => openItemModal(l, 'edit')}>✎ Edit item</button>
                                  <button type="button" className="btn-ghost" style={{ width: '100%', textAlign: 'left' }} data-testid="line-view-item" onClick={() => openItemModal(l, 'view')}>View item details</button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {it && (() => {
                        // Tamil name, category and HSN of the picked item, then the stock and price source.
                        const prod = catalogue.find((p) => p.name === it.product)
                        return (
                          <div className="text-[10.5px] text-ink-soft mt-[3px]" data-testid="item-info">
                            {[it.nameTamil, prod?.category, prod?.hsnCode ? `HSN ${prod.hsnCode}` : 'HSN not set'].filter(Boolean).join(' · ')} · On hand: {it.onHand.toLocaleString('en-IN')} {it.unit} · rate from {it.priceSource}
                          </div>
                        )
                      })()}
                      {!it && l.text.trim() && <div className="text-[10.5px] text-ink-soft mt-[3px]">{filteredItems(l.text).length ? `${filteredItems(l.text).length} match${filteredItems(l.text).length === 1 ? '' : 'es'} — pick one from the list` : 'No item matches — check Stock for Sale'}</div>}
                      {errors[`line-${l.id}-key`] && <div className="hint-error">{errors[`line-${l.id}-key`]}</div>}
                    </td>
                    <td>
                      <select value={l.unit} onChange={(e) => updateLine(l.id, { unit: e.target.value })} className="!w-24 !py-[5px] !px-[6px] !text-xs" style={{ marginBottom: 0 }} aria-label="Unit" data-testid="line-unit">
                        {SALE_UNITS.map((u) => <option key={u} value={u}>{u === 'pkt' ? 'pcs' : u}</option>)}
                      </select>
                    </td>
                    <td className="num">
                      <div className="inline-flex items-center gap-1">
                        <input type="number" min={0} step="any" inputMode="decimal" title="Quantity — decimals allowed for loose items, e.g. 0.250 kg" value={Number.isNaN(l.qty) ? '' : l.qty} onChange={(e) => updateLine(l.id, { qty: e.target.value === '' ? NaN : Number(e.target.value) })} className={`!w-24 !py-[6px] text-right ${errors[`line-${l.id}-qty`] ? '!border-danger' : ''}`} style={{ marginBottom: 0 }} />
                      </div>
                      {errors[`line-${l.id}-qty`] && <div className="hint-error">{errors[`line-${l.id}-qty`]}</div>}
                    </td>
                    <td className="num">
                      <div className="inline-flex items-center gap-1">
                        <span className="text-ink-soft text-xs">₹</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          value={rateDisplay(l)}
                          placeholder="rate or 820/20"
                          title="Selling rate per unit (incl. GST). You can type a calculation, e.g. 820/20 for a ₹820 pack of 20."
                          onChange={(e) => typeRate(l, e.target.value)}
                          onBlur={() => settleRate(l)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); settleRate(l) } }}
                          className={`!w-24 !py-[6px] text-right ${errors[`line-${l.id}-rate`] || (rateDisplay(l).trim() !== '' && Number.isNaN(l.rate)) ? '!border-danger' : ''}`}
                          style={{ marginBottom: 0 }}
                          data-testid="line-rate"
                        />
                      </div>
                      {errors[`line-${l.id}-rate`] && <div className="hint-error">{errors[`line-${l.id}-rate`]}</div>}
                      {isExpression(rateDisplay(l)) && !Number.isNaN(l.rate) && <div className="text-[10.5px] text-ink-soft">= {money(l.rate)} / {l.unit === 'pkt' ? 'pc' : l.unit}</div>}
                    </td>
                    <td className="num">
                      <select
                        value={l.gstRate}
                        onChange={(e) => updateLine(l.id, { gstRate: Number(e.target.value) })}
                        onKeyDown={(e) => {
                          // Tab out of the last field of the last filled line starts the next line at its item box.
                          if (e.key !== 'Tab' || e.shiftKey || !l.key) return
                          const idx = lines.findIndex((x) => x.id === l.id)
                          if (idx !== lines.length - 1) return
                          e.preventDefault()
                          settleRate(l)
                          addLine()
                        }}
                        className="!w-[70px] !py-[5px] !px-[6px] !text-xs" style={{ marginBottom: 0 }} aria-label="Line GST rate">
                        {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                      </select>
                    </td>
                    <td className="num font-semibold">{money((l.qty || 0) * (l.rate || 0))}</td>
                    <td className="text-center">
                      <button type="button" onClick={() => removeLine(l.id)} disabled={lines.length === 1} className="text-ink-soft hover:text-danger disabled:opacity-30 text-base leading-none" title="Remove line">×</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {errors.lines && <div className="hint-error">{errors.lines}</div>}
          <button type="button" className="btn-ghost mt-[10px]" onClick={addLine}>+ Add item</button>
          <div className="hint">Rates are tax-inclusive selling prices from the price list (or cost + 25% when no price is set); GST % comes from the product and can be changed per line. Stock is taken from the soonest-expiring batch first (FEFO).</div>

          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <label style={{ margin: 0 }}>Discount</label>
            <input type="number" step="0.01" min={0} value={discountInput} placeholder="0" onChange={(e) => { setDiscountInput(e.target.value); clearError('discount') }} className="!w-24 !py-[5px] !px-[8px] !text-xs text-right" style={{ marginBottom: 0 }} data-testid="discount-input" title="Discount on the invoice, taken off the sub total and spread over the items" />
            <select value={discountMode} onChange={(e) => setDiscountMode(e.target.value as 'percent' | 'amount')} className="!w-14 !py-[5px] !px-[4px] !text-xs" style={{ marginBottom: 0 }} data-testid="discount-mode" title="Discount as a percentage or a rupee amount">
              <option value="percent">%</option>
              <option value="amount">₹</option>
            </select>
            <span className="text-ink-soft text-xs" data-testid="discount-amount">{discountAmount > 0 ? `− ${money(discountAmount)} off the sub total (spread over the items, GST adjusted)` : 'Optional — taken off the sub total'}</span>
          </div>
          {errors.discount && <div className="hint-error">{errors.discount}</div>}
          <div className="grid grid-cols-4 gap-3 mt-3">
            <div>
              <label>Sub Total (Tax Inclusive)</label>
              <input value={money(subTotal)} disabled />
              <div className="hint">{discountAmount > 0 ? `Before discount · after discount ${money(afterDiscount)}` : 'Sum of line amounts'}</div>
            </div>
            <div>
              <label>GST included</label>
              <input value={money(gstAmount)} disabled />
              <div className="hint">CGST {money(Math.round(gstAmount * 50) / 100)} + SGST {money(Math.round((gstAmount - Math.round(gstAmount * 50) / 100) * 100) / 100)}</div>
            </div>
            <div>
              <label>Round Off</label>
              <input value={`${roundOff < 0 ? '-' : ''}${money(Math.abs(roundOff))}`} disabled />
              <div className="hint">To the nearest rupee</div>
            </div>
            <div>
              <label>Total</label>
              <input value={money(total)} disabled className="!font-bold !text-[15px]" />
              <div className="hint">Invoice value</div>
            </div>
          </div>

          <div className="mt-3">
            <label>Payment Type</label>
            {isWalkin ? (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <button type="button" className={paymentType === 'Cash' ? 'btn-primary' : 'btn-ghost'} onClick={() => choosePaymentType('Cash')}>Cash</button>
                  <button type="button" className={paymentType === 'UPI' ? 'btn-primary' : 'btn-ghost'} onClick={() => choosePaymentType('UPI')}>UPI</button>
                  {paymentType === 'UPI' ? (
                    <input value={upiRef} onChange={(e) => { setUpiRef(e.target.value); clearError('paymentType') }} placeholder="UPI transaction ref (optional)" style={{ marginBottom: 0 }} />
                  ) : (
                    <div />
                  )}
                </div>
                <div className="hint">Walk-in customers pay at the counter — the {paymentType === 'UPI' ? 'UPI' : 'cash'} receipt posts to Collections automatically.</div>
              </>
            ) : selectedStore ? (
              <>
                <div className="flex items-center gap-3 rounded-[var(--radius)] px-4 py-3" style={{ background: 'var(--accent-soft)' }}>
                  <span className="badge badge-credit">Credit</span>
                  <div className="text-[13px]">
                    <span className="font-semibold">{formatINR(total)}</span> goes to <span className="font-semibold">{selectedStore.name}</span>'s credit ledger · due in 30 days
                    {selectedStore.outstanding > 0 && <span className="text-ink-soft"> · current outstanding {formatINR(selectedStore.outstanding)}</span>}
                  </div>
                </div>
                <div className="hint">Registered stores are billed on credit; collect against the invoice from Sales &amp; Collections or the invoice page.</div>
              </>
            ) : (
              <div className="hint">Pick a registered store (billed on credit) or a walk-in customer (pays by cash / UPI).</div>
            )}
            {errors.paymentType && <div className="hint-error">{errors.paymentType}</div>}
          </div>

          <div className="btnrow">
            {isDraftEdit ? (
              <>
                <button type="button" className="btn-accent" onClick={() => generate({ submitAfterSave: true })} disabled={submitting} data-testid="generate-invoice" title="Save these lines, allocate stock and take the next invoice number">
                  {submitting ? 'Working…' : `Save & generate invoice ${nextInvoiceNo}`}
                </button>
                <button type="button" className="btn-ghost" onClick={() => generate({ draft: true })} disabled={submitting} data-testid="save-draft" title="Keep it as a draft — rates may still be blank">
                  Save draft {editing?.invoiceNo || ''}
                </button>
              </>
            ) : (
              <button type="button" className="btn-accent" onClick={() => generate()} disabled={submitting || (!!editId && !editing)} data-testid="generate-invoice">
                {submitting ? (editId ? 'Saving…' : 'Generating…') : editId ? `Save changes to ${editing?.invoiceNo || 'invoice'}` : `Generate Invoice ${nextInvoiceNo}`}
              </button>
            )}
            {!editId && (
              <button type="button" className="btn-ghost" onClick={() => generate({ draft: true })} disabled={submitting} data-testid="save-draft" title="Park this sale — rates can be left blank and no stock moves until it is generated">
                Save as draft (rates optional)
              </button>
            )}
            {editId && <button type="button" className="btn-ghost" onClick={cancelEdit} disabled={submitting}>Cancel</button>}
          </div>
        </div>

        <div className="card mt-[18px]">
          <div className="field-label-row">
            <h2 style={{ marginBottom: 0 }}>Today's Invoices</h2>
            <Link to="/dashboard/sales/invoices/list" className="text-[12px] font-semibold text-primary-500 hover:underline">View all invoices →</Link>
          </div>
          {todaySummary && (
            <div className="hint" style={{ marginTop: 4, marginBottom: 10 }}>
              {todaySummary.invoices} invoice{todaySummary.invoices === 1 ? '' : 's'} · {formatINR(todaySummary.amount)} · Cash/UPI {formatINR(todaySummary.cash)} · Credit {formatINR(todaySummary.credit)}
            </div>
          )}
          <table>
            <thead>
              <tr><th>Invoice</th><th>Store</th><th className="num">Amount</th><th>Type</th></tr>
            </thead>
            <tbody>
              {todaysInvoices.length === 0 && (
                <tr><td colSpan={4} className="text-ink-soft">No invoices yet today — the first sale you generate appears here.</td></tr>
              )}
              {todaysInvoices.map((inv) => (
                <tr key={inv.id}>
                  <td><Link to={`/dashboard/sales/invoices/${inv.id}`} className="font-semibold text-primary-500 hover:underline">{inv.invoiceNo}</Link></td>
                  <td>{inv.customerName}</td>
                  <td className="num">{formatINR(inv.amount)}</td>
                  <td><span className={`badge ${inv.type === 'Credit' ? 'badge-credit' : 'badge-cash'}`}>{inv.type}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
